<template>
  <div :class="['vdb-page', { 'vdb-page--transparent': isCompactEmbed }]">
    <!-- Trial Banner -->
    <div v-if="trialDaysLeft !== null && !hideSiteChrome" class="vdb-trial-banner">
      <span>Your Pro trial ends in <strong>{{ trialDaysLeft }} day{{ trialDaysLeft !== 1 ? 's' : '' }}</strong></span>
      <router-link to="/subscription" class="vdb-trial-banner-link">Manage subscription</router-link>
    </div>

    <!-- VDB Header (public pages) -->
    <VdbHeader v-if="!hideSiteChrome" @search="focusSearch" />

    <!-- Main Content -->
    <main class="flex-1" :class="{ 'vdb-app-main': !isEmbedRoute }">
      <router-view />
    </main>

    <!-- VDB Footer (public pages) -->
    <VdbFooter v-if="!hideSiteChrome && !isAdminRoute" />

    <!-- Creator Panel Tab (right edge) -->
    <button
      v-if="!hideSiteChrome && !creatorPanelOpen"
      @click="creatorPanelOpen = true"
      class="cp-tab"
      :class="{ 'vdb-mobile-blog-utility': isBlogRoute }"
      title="Open Creator Panel"
    >
      <div class="cp-tab-inner">
        <span class="cp-tab-bolt">&#9889;</span>
        <span class="cp-tab-label"><em>Creator</em> Panel</span>
      </div>
    </button>

    <!-- Creator Panel Drawer -->
    <div
      v-if="!hideSiteChrome && creatorPanelOpen"
      class="vdb-creator-panel"
      :class="{ 'w-[50vw]': creatorPanelExpanded, 'vdb-mobile-blog-utility': isBlogRoute }"
    >
      <!-- Header: ink-800 with grain overlay -->
      <div class="cp-header">
        <div class="cp-header-grain"></div>
        <div class="cp-header-left">
          <span class="cp-header-bolt">&#9889;</span>
          <span class="cp-header-title"><em>Creator</em> Panel</span>
        </div>
        <div class="cp-header-right">
          <button @click="creatorPanelOpen = false" class="cp-icon-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>
      <!-- Body -->
      <div class="cp-body">
        <CreatorPanel :videoId="currentVideoId" />
      </div>
    </div>

    <!-- Cookie Notice -->
    <CookieNotice
      v-if="!isEmbedRoute"
      @cookies-accepted="handleCookiesAccepted"
      @cookies-declined="handleCookiesDeclined"
    />

    <!-- Global Toast Container -->
    <ToastContainer />
    <LlmBudgetModal />

    <!-- Strategy Chat Panel -->
    <ChatPanel
      v-if="showStrategyChat"
      :currentVideo="selectedVideo"
      :suppress-on-mobile="isBlogRoute"
      @execute-action="handleChatAction"
    />

    <!-- Global Video Detail Modal -->
    <VideoDetailModal
      v-if="selectedVideo"
      :key="selectedVideo.id"
      :video="selectedVideo"
      @close="closeVideoModal"
    />

    <!-- Signup Popup Modal -->
    <SignUpModal
      v-if="!hideSiteChrome"
      :show="showSignupModal"
      :feed-context="signupFeedContext"
      @close="closeSignupModal"
    />

    <!-- Interest Onboarding Modal -->
    <InterestOnboardingModal
      v-if="!hideSiteChrome"
      :show="showInterestOnboarding"
      :is-authenticated="isAuthenticated"
      @close="closeInterestOnboarding"
      @signup-needed="onInterestSignupNeeded"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import VdbHeader from '@/components/vdb/VdbHeader.vue';
import VdbFooter from '@/components/vdb/VdbFooter.vue';
const CreatorPanel = defineAsyncComponent(() => import('@/components/CreatorPanel.vue'));
import CookieNotice from '@/components/CookieNotice.vue';
import ToastContainer from '@/components/ToastContainer.vue';
import LlmBudgetModal from '@/components/LlmBudgetModal.vue';
const ChatPanel = defineAsyncComponent(() => import('@/components/ChatPanel.vue'));
const VideoDetailModal = defineAsyncComponent(() => import('@/components/VideoDetailModal.vue'));
const SignUpModal = defineAsyncComponent(() => import('@/components/SignUpModal.vue'));
const InterestOnboardingModal = defineAsyncComponent(() => import('@/components/InterestOnboardingModal.vue'));
import { authManager } from '@/services/auth.js';
import tierService from '@/services/tierService.js';
import api, { videoApi } from '@/services/api.js';
import { useAdminNotifications } from '@/composables/useAdminNotifications.js';
import { trackSignupCompletedOnce } from '@/services/analytics.js';

const PENDING_INTERESTS_KEY = 'pending_interests';
const INTEREST_MODAL_DISMISSED_KEY = 'interest_modal_dismissed';
const INTEREST_MODAL_LAST_SHOWN_KEY = 'interest_modal_last_shown';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export default {
  name: "App",
  components: {
    VdbHeader,
    VdbFooter,
    ChatPanel,
    CreatorPanel,
    CookieNotice,
    ToastContainer,
    LlmBudgetModal,
    VideoDetailModal,
    SignUpModal,
    InterestOnboardingModal,
  },
  setup() {
    const { totalAdmin, totalCreators, refresh, setEnabled } = useAdminNotifications();
    return { totalAdmin, totalCreators, refreshAdminNotifications: refresh, setAdminNotificationsEnabled: setEnabled };
  },
  provide() {
    return {
      refreshAdminNotifications: this.refreshAdminNotifications,
      openCreatorPanel: (videoId) => {
        if (videoId) this.panelVideoOverride = videoId;
        this.creatorPanelOpen = true;
      },
    };
  },
  data() {
    return {
      mobileMenuOpen: false,
      taxonomyDropdownOpen: false,
      adminDropdownOpen: false,
      premiumDropdownOpen: false,
      browseDropdownOpen: false,
      creatorsDropdownOpen: false,
      profileDropdownOpen: false,
      creatorPanelOpen: false,
      creatorPanelExpanded: false,
      panelVideoOverride: null,
      creatorPanelVideoMeta: null,
      isAuthenticated: false,
      userEmail: '',
      userTier: null,
      globalSearchQuery: '',
      selectedVideo: null,
      showSignupModal: false,
      signupFeedContext: null,
      signupPopupTimeout: null,
      showInterestOnboarding: false,
      interestModalShownThisSession: false,
      interestOnboardingTimer: null,
      interestOnboardingCheckedUserId: null,
      unsubscribeAuth: null,
      hasHandledFirstRoute: false,
    };
  },
  computed: {
    isEmbedRoute() {
      if (this.$route?.meta?.embed === true) return true;
      const embedQuery = this.$route?.query?.embed;
      if (embedQuery === undefined || embedQuery === null) return false;
      if (typeof embedQuery === "string") {
        const normalized = embedQuery.trim().toLowerCase();
        return ["1", "true", "yes", "embed"].includes(normalized);
      }
      return embedQuery === 1 || embedQuery === true;
    },
    // Bare routes (meta.bare) render only the page: no header, footer,
    // banners, panels or signup prompts. Analytics still run, unlike embeds.
    isBareRoute() {
      return this.$route?.meta?.bare === true;
    },
    hideSiteChrome() {
      return this.isEmbedRoute || this.isBareRoute;
    },
    isCompactEmbed() {
      return this.$route?.path === '/cut30embed-compact';
    },
    isAdminRoute() {
      const path = this.$route?.path || '';
      return path.startsWith('/admin') || path === '/bot-admin' || path === '/analytics' || path === '/views' || path === '/queue' || path === '/scraper';
    },
    isBlogRoute() {
      const path = this.$route?.path || '';
      return path === '/blog' || path.startsWith('/blog/');
    },
    isAdmin() {
      return this.userTier?.tier === 'admin';
    },
    isStrategist() {
      return this.userTier?.tier === 'strategist';
    },
    isPremium() {
      return this.isStrategist;
    },
    isBasic() {
      return this.userTier?.tier === 'basic';
    },
    trialDaysLeft() {
      if (this.userTier?.subscription_status !== 'trialing' || !this.userTier?.trial_ends_at) return null;
      const ms = new Date(this.userTier.trial_ends_at) - Date.now();
      if (ms <= 0) return 0;
      return Math.ceil(ms / 86400000);
    },
    canAccessChatBot() {
      return tierService.canAccessChatBot(this.userTier);
    },
    showStrategyChat() {
      if (!this.canAccessChatBot || this.hideSiteChrome) return false;
      // The board workspace hosts its own docked ChatPanel instance.
      if (['SwipefileBoard', 'NewBoard'].includes(this.$route?.name)) return false;
      return true;
    },
    canAccessFeed() {
      return tierService.canAccessFeed(this.userTier);
    },
    canAccessAdmin() {
      return tierService.canEditTaxonomy(this.userTier);
    },
    canAccessAdvancedSubmissionFeatures() {
      return this.isStrategist || this.isAdmin;
    },
    currentYear() {
      return new Date().getFullYear();
    },
    isVideoPage() {
      return this.$route?.name === 'PublicVideoDetail';
    },
    currentVideoId() {
      if (this.panelVideoOverride) return this.panelVideoOverride;
      if (!this.isVideoPage) return null;
      const slug = this.$route?.params?.slug;
      if (!slug) return null;
      const match = String(slug).match(/^(\d+)/);
      return match ? Number(match[1]) : null;
    }
  },
  methods: {
    focusSearch() {
      this.$router.push({ path: '/videos', query: { focus: 'search' } });
    },
    async loadCreatorPanelVideoMeta() {
      const id = this.currentVideoId;
      if (!id) { this.creatorPanelVideoMeta = null; return; }
      try {
        const response = await api.get(`/videos/${id}`);
        const v = response.data?.data || response.data;
        this.creatorPanelVideoMeta = {
          thumbnail_url: v.thumbnail_url || v.thumbnail || '',
          simple_description: v.simple_description || v.overview || '',
        };
      } catch {
        this.creatorPanelVideoMeta = null;
      }
    },
    clearGlobalSearch() {
      this.globalSearchQuery = '';
    },
    onGlobalKeydown(e) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        this.focusSearch();
      }
    },
    closeDropdowns() {
      this.mobileMenuOpen = false;
      this.taxonomyDropdownOpen = false;
      this.adminDropdownOpen = false;
      this.premiumDropdownOpen = false;
      this.browseDropdownOpen = false;
      this.creatorsDropdownOpen = false;
      this.profileDropdownOpen = false;
    },
    handleGlobalSearch() {
      if (this.globalSearchQuery.trim()) {
        this.$router.push({ path: '/videos', query: { search: this.globalSearchQuery.trim() } });
        this.globalSearchQuery = '';
        this.closeDropdowns();
      }
    },
    handleChatAction(action) {
      if (action.type === 'details' && action.data?.videoId) {
        this.openVideoModal(action.data.videoId);
      }
    },
    async openVideoModal(videoId) {
      try {
        const response = await videoApi.getPublicVideo(videoId);
        if (response.data?.success && response.data.data) {
          this.selectedVideo = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching video for modal:', error);
      }
    },
    closeVideoModal() { this.selectedVideo = null; },
    handleClickOutside(event) {
      const refs = ['taxonomyDropdown', 'adminDropdown', 'profileDropdown', 'creatorsDropdown', 'browseDropdown', 'premiumDropdown'];
      refs.forEach(ref => {
        if (this.$refs[ref] && !this.$refs[ref].contains(event.target)) {
          this[ref.replace('Dropdown', 'DropdownOpen').replace('dropdown', 'DropdownOpen')] = false;
        }
      });
    },
    getTierDisplayName(tier) {
      return { admin: 'Admin', strategist: 'Strategist', creator: 'Pro', basic: 'Free' }[tier] || 'Unknown';
    },
    async handleSignOut() {
      try {
        await authManager.signOut();
        this.updateAuthState();
        this.$router.push('/login');
      } catch (error) {
        console.error('Sign out error:', error);
      }
    },
    async updateAuthState() {
      this.isAuthenticated = authManager.isAuthenticated;
      this.userEmail = authManager.user?.email || '';
      if (authManager.isAuthenticated && authManager.user) {
        await this.loadUserTier();
        if (this.canAccessAdmin) this.refreshAdminNotifications();
      } else {
        this.userTier = null;
        this.scheduleInterestModal();
      }
      if (this.isAuthenticated) {
        trackSignupCompletedOnce({ user: authManager.user });
        this.showSignupModal = false;
        this.showInterestOnboarding = false;
        if (this.signupPopupTimeout) {
          clearTimeout(this.signupPopupTimeout);
          this.signupPopupTimeout = null;
        }
        await this.applyPendingInterests();
        this.scheduleInterestOnboardingCheck();
      }
    },
    scheduleInterestOnboardingCheck() {
      const userId = authManager.user?.id;
      if (!userId || this.interestOnboardingCheckedUserId === userId) return;
      if (this.interestOnboardingTimer) clearTimeout(this.interestOnboardingTimer);
      this.interestOnboardingTimer = setTimeout(() => {
        this.interestOnboardingTimer = null;
        this.checkInterestOnboarding();
      }, 3000);
    },
    async loadUserTier() {
      try {
        if (authManager.user?.id) {
          this.userTier = await tierService.getUserTier(authManager.user.id);
        }
      } catch (error) {
        console.error('Error loading user tier:', error);
        this.userTier = null;
      }
    },
    handleCookiesAccepted() {},
    handleCookiesDeclined() {},
    closeSignupModal() {
      this.showSignupModal = false;
      this.signupFeedContext = null;
    },
    closeInterestOnboarding() {
      this.showInterestOnboarding = false;
      this.interestModalShownThisSession = true;
    },
    onInterestSignupNeeded(context) {
      this.showInterestOnboarding = false;
      this.interestModalShownThisSession = true;
      this.signupFeedContext = context || null;
      this.showSignupModal = true;
    },
    scheduleInterestModal() {
      if (this.isEmbedRoute || this.isAuthenticated) return;
      if (this.$route?.meta?.skipInterestModal) return;
      if (document.documentElement.dataset.crawler === '1') return;
      if (!sessionStorage.getItem('session_has_navigated')) return;
      // Suppress interest modal on video pages — the video gate handles signup CTA there
      if (this.$route?.name === 'PublicVideoDetail') return;
      if (localStorage.getItem(INTEREST_MODAL_DISMISSED_KEY)) return;
      const lastShown = localStorage.getItem(INTEREST_MODAL_LAST_SHOWN_KEY);
      if (lastShown && (Date.now() - parseInt(lastShown, 10)) < THIRTY_DAYS_MS) return;
      if (this.interestModalShownThisSession) return;
      if (this.signupPopupTimeout) clearTimeout(this.signupPopupTimeout);
      this.signupPopupTimeout = setTimeout(() => {
        this.signupPopupTimeout = null;
        if (!this.isAuthenticated && this.$route?.name !== 'PublicVideoDetail') {
          this.showInterestOnboarding = true;
          this.interestModalShownThisSession = true;
          localStorage.setItem(INTEREST_MODAL_LAST_SHOWN_KEY, Date.now().toString());
        }
      }, 5000);
    },
    async applyPendingInterests() {
      const raw = localStorage.getItem(PENDING_INTERESTS_KEY);
      if (!raw) return;
      try {
        const { text, accountTypeIds } = JSON.parse(raw);
        localStorage.removeItem(PENDING_INTERESTS_KEY);
        if (text) await videoApi.parseInterests(text, accountTypeIds || []).catch(() => {});
      } catch {
        localStorage.removeItem(PENDING_INTERESTS_KEY);
      }
    },
    async checkInterestOnboarding() {
      try {
        const userId = authManager.user?.id;
        if (!userId || this.interestOnboardingCheckedUserId === userId) return;
        if (this.$route?.meta?.skipInterestModal) return;
        if (localStorage.getItem('invite_signup_in_progress')) {
          localStorage.removeItem('invite_signup_in_progress');
          this.interestOnboardingCheckedUserId = userId;
          return;
        }
        if (!this.userTier) return;
        this.interestOnboardingCheckedUserId = userId;
        if (['admin', 'strategist'].includes(this.userTier.tier)) return;
        const completed = !!this.userTier.interests_completed_at || !!this.userTier.onboarding_completed_at;
        const dismissed = !!this.userTier.onboarding_dismissed_at;
        if (completed || dismissed) return;
        this.showInterestOnboarding = true;
      } catch (error) {
        console.error('Error checking interest onboarding:', error);
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onGlobalKeydown);
    document.addEventListener("click", this.handleClickOutside);
    this.updateAuthState();
    this.unsubscribeAuth = authManager.addListener(this.updateAuthState);
  },
  beforeUnmount() {
    document.body.style.overflow = '';
    window.removeEventListener('keydown', this.onGlobalKeydown);
    document.removeEventListener("click", this.handleClickOutside);
    if (this.signupPopupTimeout) clearTimeout(this.signupPopupTimeout);
    if (this.interestOnboardingTimer) clearTimeout(this.interestOnboardingTimer);
    if (this.unsubscribeAuth) this.unsubscribeAuth();
  },
  watch: {
    isAdmin: {
      immediate: true,
      handler(isAdmin) {
        this.setAdminNotificationsEnabled(isAdmin);
      },
    },
    mobileMenuOpen(isOpen) {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    },
    $route(to) {
      this.panelVideoOverride = null;
      this.closeDropdowns();
      if (to.name === 'PublicVideoDetail') {
        if (this.signupPopupTimeout) {
          clearTimeout(this.signupPopupTimeout);
          this.signupPopupTimeout = null;
        }
        this.showInterestOnboarding = false;
      }
      if (!this.hasHandledFirstRoute) {
        this.hasHandledFirstRoute = true;
      } else {
        sessionStorage.setItem('session_has_navigated', '1');
        if (!this.isAuthenticated) this.scheduleInterestModal();
      }
      if (to.name === 'PublicVideoDetail' && this.isAuthenticated) {
        this.loadCreatorPanelVideoMeta();
      } else {
        this.creatorPanelVideoMeta = null;
      }
      if (this.canAccessAdmin && this.$route.path.startsWith('/admin')) {
        this.refreshAdminNotifications();
      }
    },
  },
};
</script>

<style>
html.cut30-compact-embed,
body.cut30-compact-embed,
body.cut30-compact-embed #app,
.vdb-page.vdb-page--transparent {
  background: transparent !important;
  background-image: none !important;
}

.vdb-page.vdb-page--transparent::before {
  display: none;
}

::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: var(--cream-100);
}
::-webkit-scrollbar-thumb {
  background: var(--cream-500);
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--ink-300);
}

.vdb-trial-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 20px;
  background: var(--cream-200);
  color: var(--ink-500);
  font-family: var(--font-editorial);
  font-size: 14px;
  line-height: 1.4;
  border-bottom: 1px solid var(--line-100);
}

.vdb-trial-banner-link {
  color: var(--ink-500);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.vdb-trial-banner-link:hover {
  color: var(--ink-400);
}

@media (max-width: 480px) {
  .vdb-trial-banner {
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
    padding: 8px 16px;
  }
}
</style>
